import { useState, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { Plus, Pencil, Trash2, Search, CheckCircle, Circle, Loader2, Package, Tag, Palette, Layers, HardDrive } from 'lucide-react'
import {
  Card,
  CardContent,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Badge,
  Separator,
} from '@jira-clone/ui'
import {
  useAssetCategoryMasters,
  useAssetCategoryMutations,
  useAssetStatusMasters,
  useAssetStatusMutations,
  type AssetCategoryMaster,
  type AssetStatusMaster,
} from '../../shared/hooks/useAssetMasterData'
import { getApiErrorMessage } from '../../shared/utils/apiError'

const PHYSICAL_ICONS = [
  'Laptop', 'Monitor', 'Keyboard', 'Mouse', 'Headphones', 'Video', 'Tablet', 'Smartphone',
  'Server', 'Wifi', 'Battery', 'Sofa', 'Layout', 'Users', 'ArchiveIcon', 'Printer',
  'SearchIcon', 'Projector', 'Copy', 'Wind', 'Car', 'Zap', 'Package',
]
const DIGITAL_ICONS = ['Image', 'FileText', 'ShieldCheck', 'Megaphone', 'FolderOpen', 'Package']
const ALL_ICONS = Array.from(new Set([...PHYSICAL_ICONS, ...DIGITAL_ICONS]))

// ─── Category Draft ──────────────────────────────────────────────────────

interface CategoryDraft {
  id?: string
  name: string
  code: string
  label: string
  assetType: 'PHYSICAL' | 'DIGITAL'
  icon?: string
  sortOrder: number
  isActive: boolean
  isDefault: boolean
}

const EMPTY_CATEGORY_DRAFT: CategoryDraft = {
  name: '',
  code: '',
  label: '',
  assetType: 'PHYSICAL',
  icon: '',
  sortOrder: 0,
  isActive: true,
  isDefault: false,
}

// ─── Status Draft ────────────────────────────────────────────────────────

interface StatusDraft {
  id?: string
  name: string
  code: string
  label: string
  assetType: 'PHYSICAL' | 'DIGITAL'
  color: string
  sortOrder: number
  isActive: boolean
  isDefault: boolean
}

const EMPTY_STATUS_DRAFT: StatusDraft = {
  name: '',
  code: '',
  label: '',
  assetType: 'PHYSICAL',
  color: '#6b7280',
  sortOrder: 0,
  isActive: true,
  isDefault: false,
}

const STATUS_COLOR_PRESETS = [
  { label: 'Green', value: '#10b981' },
  { label: 'Blue', value: '#3b82f6' },
  { label: 'Amber', value: '#f59e0b' },
  { label: 'Red', value: '#ef4444' },
  { label: 'Gray', value: '#6b7280' },
  { label: 'Purple', value: '#8b5cf6' },
  { label: 'Teal', value: '#14b8a6' },
  { label: 'Pink', value: '#ec4899' },
]

const PRESET_CATEGORIES: Record<string, CategoryDraft[]> = {
  PHYSICAL: [
    { name: 'Laptop', code: 'LAPTOP', label: 'Laptop', assetType: 'PHYSICAL', icon: 'Laptop', sortOrder: 1, isActive: true, isDefault: true },
    { name: 'Desktop PC', code: 'DESKTOP_PC', label: 'Desktop PC', assetType: 'PHYSICAL', icon: 'Monitor', sortOrder: 2, isActive: true, isDefault: true },
    { name: 'Monitor', code: 'MONITOR', label: 'Monitor', assetType: 'PHYSICAL', icon: 'Monitor', sortOrder: 3, isActive: true, isDefault: true },
    { name: 'Keyboard', code: 'KEYBOARD', label: 'Keyboard', assetType: 'PHYSICAL', icon: 'Keyboard', sortOrder: 4, isActive: true, isDefault: true },
    { name: 'Mouse', code: 'MOUSE', label: 'Mouse', assetType: 'PHYSICAL', icon: 'Mouse', sortOrder: 5, isActive: true, isDefault: true },
    { name: 'Headset', code: 'HEADSET', label: 'Headset', assetType: 'PHYSICAL', icon: 'Headphones', sortOrder: 6, isActive: true, isDefault: true },
    { name: 'Webcam', code: 'WEBCAM', label: 'Webcam', assetType: 'PHYSICAL', icon: 'Video', sortOrder: 7, isActive: true, isDefault: true },
    { name: 'Tablet', code: 'TABLET', label: 'Tablet', assetType: 'PHYSICAL', icon: 'Tablet', sortOrder: 8, isActive: true, isDefault: true },
    { name: 'Mobile Phone', code: 'MOBILE_PHONE', label: 'Mobile Phone', assetType: 'PHYSICAL', icon: 'Smartphone', sortOrder: 9, isActive: true, isDefault: true },
    { name: 'Server', code: 'SERVER', label: 'Server', assetType: 'PHYSICAL', icon: 'Server', sortOrder: 10, isActive: true, isDefault: true },
    { name: 'Networking Equipment', code: 'NETWORKING_EQUIPMENT', label: 'Networking Equipment', assetType: 'PHYSICAL', icon: 'Wifi', sortOrder: 11, isActive: true, isDefault: true },
    { name: 'UPS', code: 'UPS', label: 'UPS', assetType: 'PHYSICAL', icon: 'Battery', sortOrder: 12, isActive: true, isDefault: true },
    { name: 'Office Chair', code: 'OFFICE_CHAIR', label: 'Office Chair', assetType: 'PHYSICAL', icon: 'Sofa', sortOrder: 13, isActive: true, isDefault: true },
    { name: 'Office Desk', code: 'OFFICE_DESK', label: 'Office Desk', assetType: 'PHYSICAL', icon: 'Layout', sortOrder: 14, isActive: true, isDefault: true },
    { name: 'Conference Table', code: 'CONFERENCE_TABLE', label: 'Conference Table', assetType: 'PHYSICAL', icon: 'Users', sortOrder: 15, isActive: true, isDefault: true },
    { name: 'Cabinet', code: 'CABINET', label: 'Cabinet', assetType: 'PHYSICAL', icon: 'ArchiveIcon', sortOrder: 16, isActive: true, isDefault: true },
    { name: 'Printer', code: 'PRINTER', label: 'Printer', assetType: 'PHYSICAL', icon: 'Printer', sortOrder: 17, isActive: true, isDefault: true },
    { name: 'Scanner', code: 'SCANNER', label: 'Scanner', assetType: 'PHYSICAL', icon: 'SearchIcon', sortOrder: 18, isActive: true, isDefault: true },
    { name: 'Projector', code: 'PROJECTOR', label: 'Projector', assetType: 'PHYSICAL', icon: 'Projector', sortOrder: 19, isActive: true, isDefault: true },
    { name: 'Photocopier', code: 'PHOTOCOPIER', label: 'Photocopier', assetType: 'PHYSICAL', icon: 'Copy', sortOrder: 20, isActive: true, isDefault: true },
    { name: 'Air Conditioner', code: 'AIR_CONDITIONER', label: 'Air Conditioner', assetType: 'PHYSICAL', icon: 'Wind', sortOrder: 21, isActive: true, isDefault: true },
    { name: 'Vehicle', code: 'VEHICLE', label: 'Vehicle', assetType: 'PHYSICAL', icon: 'Car', sortOrder: 22, isActive: true, isDefault: true },
    { name: 'Electrical Equipment', code: 'ELECTRICAL_EQUIPMENT', label: 'Electrical Equipment', assetType: 'PHYSICAL', icon: 'Zap', sortOrder: 23, isActive: true, isDefault: true },
    { name: 'Other (Physical)', code: 'OTHER_PHYSICAL', label: 'Other (Physical)', assetType: 'PHYSICAL', icon: 'Package', sortOrder: 24, isActive: true, isDefault: true },
  ],
  DIGITAL: [
    { name: 'Brand Assets', code: 'BRAND_ASSETS', label: 'Brand Assets', assetType: 'DIGITAL', icon: 'Image', sortOrder: 1, isActive: true, isDefault: true },
    { name: 'Sales Documents', code: 'SALES_DOCUMENTS', label: 'Sales Documents', assetType: 'DIGITAL', icon: 'FileText', sortOrder: 2, isActive: true, isDefault: true },
    { name: 'Legal & Compliance', code: 'LEGAL_COMPLIANCE', label: 'Legal & Compliance', assetType: 'DIGITAL', icon: 'ShieldCheck', sortOrder: 3, isActive: true, isDefault: true },
    { name: 'Marketing', code: 'MARKETING', label: 'Marketing', assetType: 'DIGITAL', icon: 'Megaphone', sortOrder: 4, isActive: true, isDefault: true },
    { name: 'Company Documents', code: 'COMPANY_DOCUMENTS', label: 'Company Documents', assetType: 'DIGITAL', icon: 'FolderOpen', sortOrder: 5, isActive: true, isDefault: true },
  ],
}

const PRESET_STATUSES: Record<string, StatusDraft[]> = {
  PHYSICAL: [
    { name: 'Available', code: 'AVAILABLE', label: 'Available', assetType: 'PHYSICAL', color: '#10b981', sortOrder: 1, isActive: true, isDefault: true },
    { name: 'Assigned', code: 'ASSIGNED', label: 'Assigned', assetType: 'PHYSICAL', color: '#3b82f6', sortOrder: 2, isActive: true, isDefault: true },
    { name: 'Under Maintenance', code: 'UNDER_MAINTENANCE', label: 'Under Maintenance', assetType: 'PHYSICAL', color: '#f59e0b', sortOrder: 3, isActive: true, isDefault: true },
    { name: 'Damaged', code: 'DAMAGED', label: 'Damaged', assetType: 'PHYSICAL', color: '#ef4444', sortOrder: 4, isActive: true, isDefault: true },
    { name: 'Lost', code: 'LOST', label: 'Lost', assetType: 'PHYSICAL', color: '#ef4444', sortOrder: 5, isActive: true, isDefault: true },
    { name: 'Retired', code: 'RETIRED', label: 'Retired', assetType: 'PHYSICAL', color: '#6b7280', sortOrder: 6, isActive: true, isDefault: true },
    { name: 'Disposed', code: 'DISPOSED', label: 'Disposed', assetType: 'PHYSICAL', color: '#6b7280', sortOrder: 7, isActive: true, isDefault: true },
  ],
  DIGITAL: [
    { name: 'Active', code: 'ACTIVE', label: 'Active', assetType: 'DIGITAL', color: '#10b981', sortOrder: 1, isActive: true, isDefault: true },
    { name: 'Archived', code: 'ARCHIVED', label: 'Archived', assetType: 'DIGITAL', color: '#f59e0b', sortOrder: 2, isActive: true, isDefault: true },
    { name: 'Expired', code: 'EXPIRED', label: 'Expired', assetType: 'DIGITAL', color: '#ef4444', sortOrder: 3, isActive: true, isDefault: true },
  ],
}

export default function AssetMasterDataPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const assetTypeFilter = (searchParams.get('assetType') as 'PHYSICAL' | 'DIGITAL' | null) || 'PHYSICAL'
  const [assetType, setAssetType] = useState<'PHYSICAL' | 'DIGITAL'>(assetTypeFilter)
  const [search, setSearch] = useState('')

  // Categories
  const { data: categories = [], isLoading: categoriesLoading } = useAssetCategoryMasters({
    assetType,
    isActive: true,
  })
  const { create: createCategory, update: updateCategory, setStatus: setCategoryStatus, bulkActivate: bulkCategory, remove: removeCategory } = useAssetCategoryMutations()

  // Statuses
  const { data: statuses = [], isLoading: statusesLoading } = useAssetStatusMasters({
    assetType,
    isActive: true,
  })
  const { create: createStatus, update: updateStatus, setStatus: setStatusStatus, bulkActivate: bulkStatus, remove: removeStatus } = useAssetStatusMutations()

  // Category dialog
  const [catDialogOpen, setCatDialogOpen] = useState(false)
  const [catDraft, setCatDraft] = useState<CategoryDraft>(EMPTY_CATEGORY_DRAFT)
  const [catEditingId, setCatEditingId] = useState<string | null>(null)

  // Status dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false)
  const [statusDraft, setStatusDraft] = useState<StatusDraft>(EMPTY_STATUS_DRAFT)
  const [statusEditingId, setStatusEditingId] = useState<string | null>(null)

  // Include inactive
  const [showInactive, setShowInactive] = useState(false)

  // Reload all queries when assetType or showInactive changes
  const allCategoriesQuery = useAssetCategoryMasters({ assetType, isActive: showInactive ? undefined : true })
  const allStatusesQuery = useAssetStatusMasters({ assetType, isActive: showInactive ? undefined : true })

  const allCategories = allCategoriesQuery.data || []
  const allStatuses = allStatusesQuery.data || []
  const categoriesLoadingFlag = allCategoriesQuery.isLoading
  const statusesLoadingFlag = allStatusesQuery.isLoading

  const activeCategories = allCategories.filter((c) => c.isActive)
  const activeStatuses = allStatuses.filter((s) => s.isActive)

  const catSearchLower = search.toLowerCase()
  const filteredCategories = useMemo(() => {
    let list = showInactive ? allCategories : activeCategories
    if (catSearchLower) {
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(catSearchLower) ||
          c.code.toLowerCase().includes(catSearchLower) ||
          c.label.toLowerCase().includes(catSearchLower)
      )
    }
    return list.sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code))
  }, [allCategories, activeCategories, catSearchLower, showInactive])

  const filteredStatuses = useMemo(() => {
    let list = showInactive ? allStatuses : activeStatuses
    if (catSearchLower) {
      list = list.filter(
        (s) =>
          s.name.toLowerCase().includes(catSearchLower) ||
          s.code.toLowerCase().includes(catSearchLower) ||
          s.label.toLowerCase().includes(catSearchLower)
      )
    }
    return list.sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code))
  }, [allStatuses, activeStatuses, catSearchLower, showInactive])

  // ─── Category handlers ─────────────────────────────────────────────────

  const openCreateCategory = () => {
    setCatDraft({ ...EMPTY_CATEGORY_DRAFT, assetType })
    setCatEditingId(null)
    setCatDialogOpen(true)
  }

  const openEditCategory = (cat: AssetCategoryMaster) => {
    setCatDraft({
      id: cat.id,
      name: cat.name,
      code: cat.code,
      label: cat.label,
      assetType: cat.assetType,
      icon: cat.icon || '',
      sortOrder: cat.sortOrder,
      isActive: cat.isActive,
      isDefault: cat.isDefault,
    })
    setCatEditingId(cat.id)
    setCatDialogOpen(true)
  }

  const handleSaveCategory = async () => {
    if (!catDraft.name.trim()) { toast.error('Name is required.'); return }
    if (!catDraft.code.trim()) { toast.error('Code is required.'); return }
    if (!catDraft.label.trim()) { toast.error('Label is required.'); return }

    const payload = {
      name: catDraft.name.trim(),
      code: catDraft.code.trim().toUpperCase(),
      label: catDraft.label.trim(),
      assetType: catDraft.assetType,
      icon: catDraft.icon || null,
      sortOrder: catDraft.sortOrder,
      isActive: catDraft.isActive,
      isDefault: catDraft.isDefault,
    }

    try {
      if (catEditingId) {
        await updateCategory.mutateAsync({ id: catEditingId, payload })
        toast.success('Category updated.')
      } else {
        await createCategory.mutateAsync(payload)
        toast.success('Category created.')
      }
      setCatDialogOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, catEditingId ? 'update this category' : 'create this category'))
    }
  }

  const handleDeleteCategory = async (cat: AssetCategoryMaster) => {
    if (!confirm(`Delete category "${cat.label}"? This can't be undone.`)) return
    try {
      await removeCategory.mutateAsync(cat.id)
      toast.success('Category deleted.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete this category'))
    }
  }

  const handleBulkCategoryActivate = async (isActive: boolean) => {
    const label = isActive ? 'activate' : 'deactivate'
    if (!confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} all ${assetType.toLowerCase()} asset categories?`)) return
    try {
      await bulkCategory.mutateAsync({ assetType, isActive })
      toast.success(`All ${assetType.toLowerCase()} categories ${isActive ? 'activated' : 'deactivated'}.`)
    } catch (err) {
      toast.error(getApiErrorMessage(err, `${label} all categories`))
    }
  }

  const handleSeedDefaults = async () => {
    const presets = PRESET_CATEGORIES[assetType]
    if (!presets) return
    if (!confirm(`Seed ${presets.length} default ${assetType.toLowerCase()} categories? Only missing entries will be added.`)) return
    let created = 0
    for (const cat of presets) {
      try {
        await createCategory.mutateAsync(cat)
        created++
      } catch {
        // skip duplicates
      }
    }
    toast.success(`Seeded ${created} category(ies).`)
  }

  // ─── Status handlers ───────────────────────────────────────────────────

  const openCreateStatus = () => {
    setStatusDraft({ ...EMPTY_STATUS_DRAFT, assetType, color: '#6b7280' })
    setStatusEditingId(null)
    setStatusDialogOpen(true)
  }

  const openEditStatus = (status: AssetStatusMaster) => {
    setStatusDraft({
      id: status.id,
      name: status.name,
      code: status.code,
      label: status.label,
      assetType: status.assetType,
      color: status.color,
      sortOrder: status.sortOrder,
      isActive: status.isActive,
      isDefault: status.isDefault,
    })
    setStatusEditingId(status.id)
    setStatusDialogOpen(true)
  }

  const handleSaveStatus = async () => {
    if (!statusDraft.name.trim()) { toast.error('Name is required.'); return }
    if (!statusDraft.code.trim()) { toast.error('Code is required.'); return }
    if (!statusDraft.label.trim()) { toast.error('Label is required.'); return }

    const payload = {
      name: statusDraft.name.trim(),
      code: statusDraft.code.trim().toUpperCase(),
      label: statusDraft.label.trim(),
      assetType: statusDraft.assetType,
      color: statusDraft.color,
      sortOrder: statusDraft.sortOrder,
      isActive: statusDraft.isActive,
      isDefault: statusDraft.isDefault,
    }

    try {
      if (statusEditingId) {
        await updateStatus.mutateAsync({ id: statusEditingId, payload })
        toast.success('Status updated.')
      } else {
        await createStatus.mutateAsync(payload)
        toast.success('Status created.')
      }
      setStatusDialogOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, statusEditingId ? 'update this status' : 'create this status'))
    }
  }

  const handleDeleteStatus = async (status: AssetStatusMaster) => {
    if (!confirm(`Delete status "${status.label}"? This can't be undone.`)) return
    try {
      await removeStatus.mutateAsync(status.id)
      toast.success('Status deleted.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete this status'))
    }
  }

  const handleBulkStatusActivate = async (isActive: boolean) => {
    const label = isActive ? 'activate' : 'deactivate'
    if (!confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} all ${assetType.toLowerCase()} asset statuses?`)) return
    try {
      await bulkStatus.mutateAsync({ assetType, isActive })
      toast.success(`All ${assetType.toLowerCase()} statuses ${isActive ? 'activated' : 'deactivated'}.`)
    } catch (err) {
      toast.error(getApiErrorMessage(err, `${label} all statuses`))
    }
  }

  const handleSeedStatusDefaults = async () => {
    const presets = PRESET_STATUSES[assetType]
    if (!presets) return
    if (!confirm(`Seed ${presets.length} default ${assetType.toLowerCase()} statuses? Only missing entries will be added.`)) return
    let created = 0
    for (const s of presets) {
      try {
        await createStatus.mutateAsync(s)
        created++
      } catch {
        // skip duplicates
      }
    }
    toast.success(`Seeded ${created} status(es).`)
  }

  // ─── Sync URL when switching asset type ────────────────────────────────

  const handleAssetTypeChange = (val: 'PHYSICAL' | 'DIGITAL') => {
    setAssetType(val)
    setSearchParams({ assetType: val })
    setSearch('')
  }

  return (
    <div className="space-y-5 max-w-6xl">
      {/* Header */}
      <div className="flex items-start sm:items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-primary" />
          <div>
            <h2 className="text-lg font-bold text-foreground">Asset Master Data</h2>
            <p className="text-sm text-muted-foreground">
              Manage categories and statuses for company physical and digital assets.
            </p>
          </div>
        </div>
      </div>

      {/* Asset Type Tabs */}
      <Tabs value={assetType} onValueChange={(v) => handleAssetTypeChange(v as 'PHYSICAL' | 'DIGITAL')} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="PHYSICAL" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Package className="mr-2 h-4 w-4" /> Physical Assets
          </TabsTrigger>
          <TabsTrigger value="DIGITAL" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <HardDrive className="mr-2 h-4 w-4" /> Digital Assets
          </TabsTrigger>
        </TabsList>

        <TabsContent value="PHYSICAL" className="space-y-5 pt-4">
          <MasterDataTabContent
            assetType="PHYSICAL"
            search={search}
            setSearch={setSearch}
            showInactive={showInactive}
            setShowInactive={setShowInactive}
            categories={filteredCategories}
            statuses={filteredStatuses}
            categoriesLoading={categoriesLoadingFlag}
            statusesLoading={statusesLoadingFlag}
            onOpenCreateCategory={openCreateCategory}
            onOpenCreateStatus={openCreateStatus}
            onEditCategory={openEditCategory}
            onDeleteCategory={handleDeleteCategory}
            onToggleCategoryStatus={(cat) => setCategoryStatus.mutateAsync({ id: cat.id, isActive: !cat.isActive })}
            onBulkCategoryActivate={handleBulkCategoryActivate}
            onSeedCategoryDefaults={handleSeedDefaults}
            onEditStatus={openEditStatus}
            onDeleteStatus={handleDeleteStatus}
            onToggleStatusStatus={(status) => setStatusStatus.mutateAsync({ id: status.id, isActive: !status.isActive })}
            onBulkStatusActivate={handleBulkStatusActivate}
            onSeedStatusDefaults={handleSeedStatusDefaults}
          />
        </TabsContent>

        <TabsContent value="DIGITAL" className="space-y-5 pt-4">
          <MasterDataTabContent
            assetType="DIGITAL"
            search={search}
            setSearch={setSearch}
            showInactive={showInactive}
            setShowInactive={setShowInactive}
            categories={filteredCategories}
            statuses={filteredStatuses}
            categoriesLoading={categoriesLoadingFlag}
            statusesLoading={statusesLoadingFlag}
            onOpenCreateCategory={openCreateCategory}
            onOpenCreateStatus={openCreateStatus}
            onEditCategory={openEditCategory}
            onDeleteCategory={handleDeleteCategory}
            onToggleCategoryStatus={(cat) => setCategoryStatus.mutateAsync({ id: cat.id, isActive: !cat.isActive })}
            onBulkCategoryActivate={handleBulkCategoryActivate}
            onSeedCategoryDefaults={handleSeedDefaults}
            onEditStatus={openEditStatus}
            onDeleteStatus={handleDeleteStatus}
            onToggleStatusStatus={(status) => setStatusStatus.mutateAsync({ id: status.id, isActive: !status.isActive })}
            onBulkStatusActivate={handleBulkStatusActivate}
            onSeedStatusDefaults={handleSeedStatusDefaults}
          />
        </TabsContent>
      </Tabs>

      {/* ─── Category Dialog ──────────────────────────────────────────── */}
      <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{catEditingId ? 'Edit Category' : 'New Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label htmlFor="cat-name">Name *</Label>
              <Input id="cat-name" value={catDraft.name} onChange={(e) => setCatDraft((d) => ({ ...d, name: e.target.value }))} placeholder="e.g. Laptop" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cat-code">Code *</Label>
              <Input id="cat-code" value={catDraft.code} onChange={(e) => setCatDraft((d) => ({ ...d, code: e.target.value.toUpperCase() }))} placeholder="e.g. LAPTOP" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cat-label">Display Label *</Label>
              <Input id="cat-label" value={catDraft.label} onChange={(e) => setCatDraft((d) => ({ ...d, label: e.target.value }))} placeholder="e.g. Laptop" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cat-icon">Icon</Label>
              <Select value={catDraft.icon || undefined} onValueChange={(v) => setCatDraft((d) => ({ ...d, icon: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select icon (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {ALL_ICONS.map((icon) => (
                    <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cat-sort">Sort Order</Label>
              <Input id="cat-sort" type="number" min={0} value={catDraft.sortOrder} onChange={(e) => setCatDraft((d) => ({ ...d, sortOrder: parseInt(e.target.value) || 0 }))} />
            </div>
            <div className="flex items-center gap-2">
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={catDraft.isActive} onChange={(e) => setCatDraft((d) => ({ ...d, isActive: e.target.checked }))} />
                <span>Active</span>
              </Label>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCatDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveCategory} disabled={createCategory.isPending || updateCategory.isPending}>
              {catEditingId ? 'Save Changes' : 'Create Category'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Status Dialog ───────────────────────────────────────────── */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{statusEditingId ? 'Edit Status' : 'New Status'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label htmlFor="status-name">Name *</Label>
              <Input id="status-name" value={statusDraft.name} onChange={(e) => setStatusDraft((d) => ({ ...d, name: e.target.value }))} placeholder="e.g. Available" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="status-code">Code *</Label>
              <Input id="status-code" value={statusDraft.code} onChange={(e) => setStatusDraft((d) => ({ ...d, code: e.target.value.toUpperCase() }))} placeholder="e.g. AVAILABLE" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="status-label">Display Label *</Label>
              <Input id="status-label" value={statusDraft.label} onChange={(e) => setStatusDraft((d) => ({ ...d, label: e.target.value }))} placeholder="e.g. Available" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="status-color">Color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  id="status-color"
                  value={statusDraft.color}
                  onChange={(e) => setStatusDraft((d) => ({ ...d, color: e.target.value }))}
                  className="h-8 w-8 rounded border border-border cursor-pointer"
                />
                <span className="text-xs text-muted-foreground font-mono">{statusDraft.color}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {STATUS_COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => setStatusDraft((d) => ({ ...d, color: preset.value }))}
                    className={`w-6 h-6 rounded-full border-2 transition-all ${statusDraft.color === preset.value ? 'border-foreground scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: preset.value }}
                    title={preset.label}
                  />
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="status-sort">Sort Order</Label>
              <Input id="status-sort" type="number" min={0} value={statusDraft.sortOrder} onChange={(e) => setStatusDraft((d) => ({ ...d, sortOrder: parseInt(e.target.value) || 0 }))} />
            </div>
            <div className="flex items-center gap-2">
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={statusDraft.isActive} onChange={(e) => setStatusDraft((d) => ({ ...d, isActive: e.target.checked }))} />
                <span>Active</span>
              </Label>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveStatus} disabled={createStatus.isPending || updateStatus.isPending}>
              {statusEditingId ? 'Save Changes' : 'Create Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ─── Shared tab content ──────────────────────────────────────────────────

function MasterDataTabContent({
  assetType,
  search,
  setSearch,
  showInactive,
  setShowInactive,
  categories,
  statuses,
  categoriesLoading,
  statusesLoading,
  onOpenCreateCategory,
  onOpenCreateStatus,
  onEditCategory,
  onDeleteCategory,
  onToggleCategoryStatus,
  onBulkCategoryActivate,
  onSeedCategoryDefaults,
  onEditStatus,
  onDeleteStatus,
  onToggleStatusStatus,
  onBulkStatusActivate,
  onSeedStatusDefaults,
}: {
  assetType: 'PHYSICAL' | 'DIGITAL'
  search: string
  setSearch: (v: string) => void
  showInactive: boolean
  setShowInactive: (v: boolean) => void
  categories: AssetCategoryMaster[]
  statuses: AssetStatusMaster[]
  categoriesLoading: boolean
  statusesLoading: boolean
  onOpenCreateCategory: () => void
  onOpenCreateStatus: () => void
  onEditCategory: (cat: AssetCategoryMaster) => void
  onDeleteCategory: (cat: AssetCategoryMaster) => void
  onToggleCategoryStatus: (cat: AssetCategoryMaster) => void
  onBulkCategoryActivate: (isActive: boolean) => void
  onSeedCategoryDefaults: () => void
  onEditStatus: (status: AssetStatusMaster) => void
  onDeleteStatus: (status: AssetStatusMaster) => void
  onToggleStatusStatus: (status: AssetStatusMaster) => void
  onBulkStatusActivate: (isActive: boolean) => void
  onSeedStatusDefaults: () => void
}) {
  const [catDeletingId, setCatDeletingId] = useState<string | null>(null)
  const [statusDeletingId, setStatusDeletingId] = useState<string | null>(null)

  return (
    <>
      {/* Categories Section */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Tag className="h-4 w-4" />
                All Categories
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {assetType === 'PHYSICAL' ? 'Company (physical) asset categories' : 'Digital asset categories'}
                {showInactive ? ' (including inactive)' : ''}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={onSeedCategoryDefaults}>
                Seed Defaults
              </Button>
              <Button variant="outline" size="sm" onClick={() => onBulkCategoryActivate(true)}>Enable All</Button>
              <Button variant="outline" size="sm" onClick={() => onBulkCategoryActivate(false)}>Disable All</Button>
              <Button size="sm" onClick={onOpenCreateCategory} className="gap-1.5">
                <Plus className="h-3.5 w-3.5" /> New
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search categories..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              Show inactive
            </label>
          </div>

          {/* Table */}
          {categoriesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No categories found. Click "New" or "Seed Defaults" to add some.
            </div>
          ) : (
            <div className="border border-border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Code</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Label</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Icon</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Order</th>
                    <th className="text-center px-3 py-2 text-xs font-medium text-muted-foreground">Status</th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {categories.map((cat) => (
                    <tr key={cat.id} className={`hover:bg-muted/30 ${!cat.isActive ? 'opacity-60' : ''}`}>
                      <td className="px-3 py-2.5 font-mono text-xs">{cat.code}</td>
                      <td className="px-3 py-2.5">{cat.label}</td>
                      <td className="px-3 py-2.5 text-xs text-muted-foreground">{cat.icon || '-'}</td>
                      <td className="px-3 py-2.5 text-xs">{cat.sortOrder}</td>
                      <td className="px-3 py-2.5 text-center">
                        <button
                          type="button"
                          onClick={() => onToggleCategoryStatus(cat)}
                          className="inline-flex items-center gap-1 text-xs"
                          title={cat.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {cat.isActive ? (
                            <CheckCircle className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <Circle className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className={cat.isActive ? 'text-emerald-600' : 'text-muted-foreground'}>
                            {cat.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </button>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => onEditCategory(cat)} title="Edit">
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          {!cat.isDefault && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setCatDeletingId(cat.id); handleDelete(cat, onDeleteCategory, setCatDeletingId) }}
                              disabled={catDeletingId === cat.id}
                              title="Delete"
                            >
                              <Trash2 className="h-3.5 w-3.5 text-destructive" />
                            </Button>
                          )}
                          {cat.isDefault && (
                            <span className="text-[10px] text-muted-foreground px-1.5">default</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statuses Section */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Palette className="h-4 w-4" />
                All Statuses
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {assetType === 'PHYSICAL' ? 'Company (physical) asset statuses' : 'Digital asset statuses'}
                {showInactive ? ' (including inactive)' : ''}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={onSeedStatusDefaults}>
                Seed Defaults
              </Button>
              <Button variant="outline" size="sm" onClick={() => onBulkStatusActivate(true)}>Enable All</Button>
              <Button variant="outline" size="sm" onClick={() => onBulkStatusActivate(false)}>Disable All</Button>
              <Button size="sm" onClick={onOpenCreateStatus} className="gap-1.5">
                <Plus className="h-3.5 w-3.5" /> New
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search statuses..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              Show inactive
            </label>
          </div>

          {/* Table */}
          {statusesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : statuses.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No statuses found. Click "New" or "Seed Defaults" to add some.
            </div>
          ) : (
            <div className="border border-border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Code</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Label</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Color</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Order</th>
                    <th className="text-center px-3 py-2 text-xs font-medium text-muted-foreground">Status</th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {statuses.map((status) => (
                    <tr key={status.id} className={`hover:bg-muted/30 ${!status.isActive ? 'opacity-60' : ''}`}>
                      <td className="px-3 py-2.5 font-mono text-xs">{status.code}</td>
                      <td className="px-3 py-2.5">{status.label}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="inline-block w-4 h-4 rounded-full border border-border" style={{ backgroundColor: status.color }} />
                          <span className="text-xs font-mono text-muted-foreground">{status.color}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-xs">{status.sortOrder}</td>
                      <td className="px-3 py-2.5 text-center">
                        <button
                          type="button"
                          onClick={() => onToggleStatusStatus(status)}
                          className="inline-flex items-center gap-1 text-xs"
                          title={status.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {status.isActive ? (
                            <CheckCircle className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <Circle className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className={status.isActive ? 'text-emerald-600' : 'text-muted-foreground'}>
                            {status.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </button>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => onEditStatus(status)} title="Edit">
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          {!status.isDefault && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setStatusDeletingId(status.id); handleDelete(status, onDeleteStatus, setStatusDeletingId) }}
                              disabled={statusDeletingId === status.id}
                              title="Delete"
                            >
                              <Trash2 className="h-3.5 w-3.5 text-destructive" />
                            </Button>
                          )}
                          {status.isDefault && (
                            <span className="text-[10px] text-muted-foreground px-1.5">default</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  )
}

// Small helper for delete-with-confirm pattern
function handleDelete<T>(
  item: T,
  onDelete: (item: T) => void,
  deletingIdSetter: React.Dispatch<React.SetStateAction<string | null>>
) {
  if (!confirm(`Delete "${(item as any).label || (item as any).name}"? This can't be undone.`)) {
    deletingIdSetter(null)
    return
  }
  onDelete(item)
}
